"use client";

import { ShieldCheck } from "lucide-react";
import { useState, useMemo } from "react";

import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import EditableCard from "@/src/components/cards/editable-card";
import FormField from "@/src/components/form-field";
import { EntityMatcher } from "@/src/components/tasks/intake-sections/matchers";
import { toast } from "@/src/hooks/use-toast";
import { useCoverageUpdateMutation, useInsuranceTopPayers } from "@/src/lib/data-fetchers/coverage-fetchers";
import { useAvailablePayers } from "@/src/lib/data-fetchers/payer-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { convertEmptyStringValuesToNull, enumToOptions } from "@/src/lib/utils/object-utils";
import { Coverage, CoverageType, CoverageErrors, validateCoverage } from "@/src/models/coverage";
import { Payer } from "@/src/models/payer";

// List of fields to validate for this card
const fieldsToValidate: (keyof Coverage)[] = [
  "coverageType",
  "customerPayer",
  "payer",
  "plan",
  "effectiveDate",
  "expirationDate",
  "subscriberId",
  "groupNumber",
  "subscriberGivenName",
  "subscriberFamilyName",
  "beneficiary",
  "relationshipToSubscriber",
  "phoneNumber",
  "faxPhoneNumber",
];

interface CoverageCardEditableProps {
  coverage: Coverage;
  onClose: () => void;
  type?: "Primary" | "Secondary";
}

export default function CoverageCardEditable({ coverage, onClose, type }: CoverageCardEditableProps) {
  const [formData, setFormData] = useState<Coverage>({
    ...coverage,
    // Default to Insurance if coverage type is null (matches the display logic in the main card)
    coverageType: coverage.coverageType || CoverageType.INSURANCE,
  });
  const [errors, setErrors] = useState<CoverageErrors>({});

  const updateCoverageMutation = useCoverageUpdateMutation();

  const { payers: availablePayers, isLoading: isLoadingPayers } = useAvailablePayers(formData?.customerPayer);

  const { topInsurancePayers } = useInsuranceTopPayers();

  const currentPayer = useMemo(() => {
    if (!formData?.customerPayer) return null;

    return (
      availablePayers.find(
        (payer) => payer.primaryPayerId === formData.customerPayer || payer.shortId === formData.customerPayer
      ) || null
    );
  }, [formData?.customerPayer, availablePayers]);

  const handlePayerSelect = (payer: Payer | null) => {
    if (payer) {
      handleInputChange("customerPayer", payer.primaryPayerId ?? "");
    } else {
      handleInputChange("customerPayer", "");
    }
  };

  const handleInputChange = (field: keyof Coverage, value: string) => {
    // Only reset fields when switching between different coverage types
    if (field === "coverageType" && formData.coverageType !== value) {
      setFormData((prev) => {
        const resetFields = fieldsToValidate.reduce<Record<string, null>>((acc, key) => {
          if (key !== "coverageType") {
            acc[key] = null;
          }

          return acc;
        }, {});

        return {
          ...prev,
          ...resetFields,
          [field]: value as CoverageType,
        };
      });

      setErrors({});

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;

        return rest;
      });
    }
  };

  const handleSave = async () => {
    const processedFormData = convertEmptyStringValuesToNull(formData);
    const { isValid, errors } = validateCoverage(processedFormData, fieldsToValidate);

    setErrors(errors);

    if (!isValid) {
      return;
    }

    try {
      await updateCoverageMutation.mutateAsync(processedFormData);

      toast({
        title: "Coverage updated",
        description: "The coverage information was successfully saved.",
        variant: "default",
      });

      onClose?.();
    } catch (error) {
      logger.error("Error saving coverage:", { error });
    }
  };

  return (
    <EditableCard
      title={`Edit ${type ? `${type} Coverage` : "Coverage"}`}
      icon={ShieldCheck}
      initialData={coverage}
      onCancel={onClose}
      onSave={handleSave}
      isSaving={updateCoverageMutation.isPending}
    >
      <div className="space-y-6">
        {/* Coverage Type */}
        <div>
          <div className="grid grid-cols-1 gap-4">
            <FormField
              label="Coverage Type"
              value={formData.coverageType}
              onChange={(value) => handleInputChange("coverageType", value)}
              type="select"
              options={enumToOptions(CoverageType)}
              error={errors.coverageType}
            />
          </div>
        </div>

        {/* Insurance Details - Only show if not self-pay */}
        {formData.coverageType === CoverageType.INSURANCE && (
          <>
            {/* Insurance Company Information */}
            <div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Insurance Payer</label>
                  <EntityMatcher
                    type="payer"
                    existingEntities={availablePayers}
                    isLoadingExistingEntities={isLoadingPayers}
                    entity={currentPayer}
                    onEntitySelected={handlePayerSelect}
                    showCreateNewOption={false}
                    weighting={topInsurancePayers}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Insurance Company"
                    value={formData.payer}
                    onChange={(value) => handleInputChange("payer", value)}
                    error={errors.payer}
                  />
                  <FormField
                    label="Plan Name"
                    value={formData.plan}
                    onChange={(value) => handleInputChange("plan", value)}
                    error={errors.plan}
                  />
                </div>
              </div>
            </div>

            {/* Dates */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Dates</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Effective Date"
                  value={formData.effectiveDate}
                  onChange={(value) => handleInputChange("effectiveDate", value)}
                  type="date"
                  error={errors.effectiveDate}
                />
                <FormField
                  label="Expiration Date"
                  value={formData.expirationDate}
                  onChange={(value) => handleInputChange("expirationDate", value)}
                  type="date"
                  error={errors.expirationDate}
                />
              </div>
            </div>

            {/* Subscriber Information */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Subscriber Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Subscriber ID"
                  value={formData.subscriberId}
                  onChange={(value) => handleInputChange("subscriberId", value)}
                  error={errors.subscriberId}
                />
                <FormField
                  label="Group Number"
                  value={formData.groupNumber}
                  onChange={(value) => handleInputChange("groupNumber", value)}
                  error={errors.groupNumber}
                />
                <FormField
                  label="Subscriber First Name"
                  value={formData.subscriberGivenName}
                  onChange={(value) => handleInputChange("subscriberGivenName", value)}
                  error={errors.subscriberGivenName}
                />
                <FormField
                  label="Subscriber Last Name"
                  value={formData.subscriberFamilyName}
                  onChange={(value) => handleInputChange("subscriberFamilyName", value)}
                  error={errors.subscriberFamilyName}
                />
                <FormField
                  label="Beneficiary"
                  value={formData.beneficiary}
                  onChange={(value) => handleInputChange("beneficiary", value)}
                  error={errors.beneficiary}
                />
                <FormField
                  label="Relationship to Subscriber"
                  value={formData.relationshipToSubscriber}
                  onChange={(value) => handleInputChange("relationshipToSubscriber", value)}
                  type="select"
                  options={enumToOptions(RelationshipToSubscriber)}
                  error={errors.relationshipToSubscriber}
                />
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(value) => handleInputChange("phoneNumber", value)}
                  type="tel"
                  error={errors.phoneNumber}
                  placeholder="555-123-4567"
                />
                <FormField
                  label="Fax Number"
                  value={formData.faxPhoneNumber}
                  onChange={(value) => handleInputChange("faxPhoneNumber", value)}
                  type="tel"
                  error={errors.faxPhoneNumber}
                  placeholder="555-123-4567"
                />
              </div>
            </div>
          </>
        )}

        {/* Self Pay Details */}
        {formData.coverageType === CoverageType.SELF_PAY && (
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Self Pay Information</h3>
            <div className="text-sm text-muted-foreground">
              This patient is self-pay. No additional insurance information is required.
            </div>
          </div>
        )}
      </div>
    </EditableCard>
  );
}
